/**
 * Rateazioni (3.15, decision of 30/10/2026): the debts paid in instalments to
 * the Agenzia delle Entrate, INPS, the Agenzia Entrate-Riscossione, a Comune
 * or another body. A page of their own: each plan has its instalments, real
 * due dates the Scadenzario will read, paid by the bank movements they are
 * reconciled with or by a card spending (3.21); an instalment due and not paid
 * is to settle.
 */
import { z } from 'zod';
import { parseIsoDate, toIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { isoDateSchema, idSchema, optionalText, requiredText } from './validation.js';
/** Who the debt is with. */
export const INSTALMENT_BODIES = ['entrate', 'inps', 'riscossione', 'comune', 'altro'];
export const INSTALMENT_BODY_LABELS = {
    entrate: 'Agenzia delle Entrate',
    inps: 'INPS',
    riscossione: 'Agenzia Entrate-Riscossione',
    comune: 'Comune',
    altro: 'Altro ente',
};
/** How the instalments are paid. */
export const INSTALMENT_METHODS = ['f24', 'pagopa', 'addebito', 'bonifico'];
export const INSTALMENT_METHOD_LABELS = {
    f24: 'F24',
    pagopa: 'Bollettino / PagoPA',
    addebito: 'Addebito in conto',
    bonifico: 'Bonifico',
};
/** Months between two instalments, for the plan made by itself. */
export const INSTALMENT_FREQUENCIES = { mensile: 1, bimestrale: 2, trimestrale: 3, semestrale: 6 };
/**
 * The same day `months` later, the last of the month when it has fewer days:
 * 31/01 → 28/02 → 31/03 keeps the day of the first instalment.
 */
export function addMonthsKeepingDay(first, months) {
    const start = parseIsoDate(first);
    const day = start.getUTCDate();
    const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, last));
    return toIsoDate(target);
}
/**
 * A plan made by itself: `count` instalments of the total, equal to the
 * cent, what is left of the division on the first; the first on its day, the
 * others every `everyMonths` months on the same day.
 */
export function instalmentSchedule(total, count, firstDue, everyMonths) {
    const cents = toCents(total);
    if (count < 1 || cents <= 0)
        return [];
    const each = Math.floor(cents / count);
    return Array.from({ length: count }, (_, index) => ({
        number: index + 1,
        dueDate: addMonthsKeepingDay(firstDue, index * everyMonths),
        amount: fromCents(index === 0 ? cents - each * (count - 1) : each),
        interest: '0.00',
    }));
}
export const INSTALMENT_STATE_LABELS = {
    pagata: 'pagata',
    da_pagare: 'da pagare',
    scaduta: 'scaduta, non pagata',
};
/** Paid when reconciled with a movement; due and not paid, overdue. */
export function instalmentState(due, today) {
    if (due.paid)
        return 'pagata';
    return due.dueDate < today ? 'scaduta' : 'da_pagare';
}
export function instalmentPlanFigures(dues, today, maxMissed) {
    const sum = (items) => fromCents(items.reduce((total, item) => total + toCents(item.amount), 0));
    const ordered = [...dues].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    const open = ordered.filter((due) => !due.paid);
    const overdue = open.filter((due) => due.dueDate < today).length;
    const next = open[0];
    return {
        total: sum(ordered),
        paid: sum(ordered.filter((due) => due.paid)),
        residual: sum(open),
        next: next ? { number: next.number, dueDate: next.dueDate, amount: next.amount } : null,
        overdue,
        atRisk: maxMissed !== null && maxMissed > 0 && overdue >= maxMissed,
    };
}
const money = z
    .string()
    .trim()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' })
    .transform((value) => fromCents(toCents(value)));
/** An instalment as the page sends it: with its id when it exists already. */
export const instalmentLineSchema = z.object({
    id: idSchema.optional(),
    number: z.number().int().min(1).max(999),
    dueDate: isoDateSchema,
    amount: money.refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    interest: money.default('0.00'),
});
/** Body of a plan, created or saved again with all its instalments. */
export const instalmentPlanSchema = z
    .object({
    subjectId: idSchema,
    body: z.enum(INSTALMENT_BODIES),
    name: requiredText(120),
    reference: optionalText(100),
    method: z.enum(INSTALMENT_METHODS),
    /** The account it is paid from, a code of the table «Conti». */
    accountCode: optionalText(30),
    /** Instalments overdue after which the plan may lapse; null: no warning. */
    maxMissed: z.number().int().min(1).max(99).nullable().default(null),
    notes: optionalText(2000),
    dues: z.array(instalmentLineSchema).min(1).max(240),
})
    .refine((plan) => new Set(plan.dues.map((due) => due.number)).size === plan.dues.length, {
    message: 'Due rate hanno lo stesso numero',
    path: ['dues'],
})
    .refine((plan) => plan.dues.every((due) => toCents(due.interest) <= toCents(due.amount)), {
    message: 'Gli interessi superano la rata',
    path: ['dues'],
});
/** Days around the due date in which a movement of its amount is proposed. */
export const INSTALMENT_PROPOSAL_DAYS = 10;
/**
 * What a card spending may be above its instalments (3.21): the commission of
 * the payment, written by the bank inside the spending.
 */
export const INSTALMENT_CARD_FEE = '2.00';
/** Why a card spending cannot pay these instalments, or null. */
export function cardInstalmentsError(spending, dues) {
    const fee = toCents(spending) - dues.reduce((sum, due) => sum + toCents(due.amount), 0);
    if (fee < 0)
        return 'Le rate scelte superano l’importo della spesa';
    if (fee > toCents(INSTALMENT_CARD_FEE)) {
        return 'La spesa supera le rate di più della commissione (al massimo 2,00 €)';
    }
    return null;
}
/**
 * The instalments a movement may pay, proposed first, then by due date. A card
 * spending has the commission inside: `fee` is what it may be above.
 */
export function instalmentChoices(movement, open, fee = '0.00') {
    const days = (date) => Math.abs(parseIsoDate(date).getTime() - parseIsoDate(movement.bookingDate).getTime()) /
        86_400_000;
    const cents = Math.abs(toCents(movement.amount));
    return open
        .map((item) => ({
        ...item,
        proposed: cents >= toCents(item.amount) &&
            cents - toCents(item.amount) <= toCents(fee) &&
            days(item.dueDate) <= INSTALMENT_PROPOSAL_DAYS,
    }))
        .sort((a, b) => Number(b.proposed) - Number(a.proposed) ||
        a.dueDate.localeCompare(b.dueDate) ||
        a.planName.localeCompare(b.planName));
}
//# sourceMappingURL=instalments.js.map